import React, { FC, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import {
  PoolSizeAdjustmentInput,
  PoolSizeAdjustmentIssue,
  PoolSizeAdjustmentReason,
  resolvePoolSizeAdjustment,
} from 'shared';

import { InfoCircle } from '../../../../assets/icons/index';
import { InfoCircleVariant } from '../../../../assets/icons/info-circle';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { FormInput } from '../../../../components/input/index';
import { useReactForm } from '../../../../hooks/index';
import {
  createDataEntryCorrectionSchema,
  parseShareAmount,
  PoolSizeContext,
} from '../adjust-pool-size.validation';
import { AdjustPoolSizeFooter } from '../components/modal-footer';
import { PoolSizeRow } from '../components/pool-size-row';

export type DataEntryCorrectionStepProps = {
  context: PoolSizeContext;
  isPending: boolean;
  onBack?: () => void;
  onCancel: () => void;
  onSubmit: (input: PoolSizeAdjustmentInput) => void;
};

export const DataEntryCorrectionStep: FC<DataEntryCorrectionStepProps> = ({
  context,
  isPending,
  onBack,
  onCancel,
  onSubmit,
}) => {
  const { currentPoolSize, grantedShares } = context;
  const schema = useMemo(
    () => createDataEntryCorrectionSchema({ currentPoolSize, grantedShares }),
    [currentPoolSize, grantedShares],
  );
  // Starts empty, not from the current size: digits typed into a pre-filled size were appended to
  // it (14,000 became 1,400,000), and a correction leaves no ledger event to trace that by.
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useReactForm({ schema, defaultValues: { dilutedSharesCount: '' } });
  const { dilutedSharesCount } = useWatch({ control });

  const resolution = resolvePoolSizeAdjustment({
    ...context,
    input: {
      reason: PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION,
      dilutedSharesCount: parseShareAmount(dilutedSharesCount),
    },
  });
  const hasAmount = resolution.issue !== PoolSizeAdjustmentIssue.INVALID_AMOUNT;

  const handleConfirm = handleSubmit((values) =>
    onSubmit({
      reason: PoolSizeAdjustmentReason.DATA_ENTRY_CORRECTION,
      dilutedSharesCount: parseShareAmount(values.dilutedSharesCount),
    }),
  );

  return (
    <>
      <div className="flex w-full flex-col gap-4">
        <div className="w-full px-4">
          <div className="flex w-full gap-3 rounded-md bg-sunbeam-50 p-3">
            <InfoCircle variant={InfoCircleVariant.WARNING} />
            <div className="flex flex-1 flex-col gap-2">
              <span className="text-sm font-[550] text-gray-800">
                <AppFormattedMessage id="GOVERNANCE_BYPASS_WARNING_TITLE" />
              </span>
              <span className="text-xs font-[450] text-gray-700">
                <AppFormattedMessage id="GOVERNANCE_BYPASS_WARNING_DESCRIPTION" />
              </span>
            </div>
          </div>
        </div>

        <PoolSizeRow
          label={<AppFormattedMessage id="CURRENT_POOL_SIZE" />}
          sharesCount={currentPoolSize}
        />

        <FormInput
          control={control}
          name="dilutedSharesCount"
          numberOnly
          placeholder={<AppFormattedMessage id="ENTER_NEW_AMOUNT_OF_SHARES" />}
          shouldFormatNumber
          wrapperClassName="w-full"
        />

        <PoolSizeRow
          delta={hasAmount ? resolution.delta : 0}
          label={<AppFormattedMessage id="NEW_POOL_SIZE" />}
          sharesCount={hasAmount ? resolution.newPoolSize : currentPoolSize}
        />
      </div>
      <AdjustPoolSizeFooter
        confirmLabel={<AppFormattedMessage id="ADJUST_POOL_SIZE" />}
        isConfirmDisabled={!isValid}
        isPending={isPending}
        onBack={onBack}
        onCancel={onCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
};
